import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { TCatalogEntities } from "types";
import { VehicleStore } from "stores";

import List from "../../List";
import Filter from "../../Filter";

import Pipeline from "../Pipeline";
import { sortAsc } from "../helper";

import { FarbeAussenReducer } from "./FarbeReducer";
import FarbeIndicator from "./FarbeIndicator";

interface IFilter {
  entities: TCatalogEntities;
  value: number[];
  dispatchFilter?: any;
}

export const SideFilter: React.FC<IFilter> = ({
  entities: { farbe: farbeEntities },
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();

  if (!farbeEntities) return null;
  const FarbeIds = Object.values(farbeEntities).map((e) => e.id);

  const setValueHandler = (value: any) => {
    if (value.length === FarbeIds.length) {
      // all variants are selected, so clean up the filter
      dispatchFilter({ type: "UPDATE_FILTER", payload: { farbe_aussen: [] } });
    } else {
      dispatchFilter({
        type: "UPDATE_FILTER",
        payload: { farbe_aussen: value },
      });
    }
  };

  const options = FarbeIds.map((id: number) => ({
    value: id,
    title: t({
      id: `farbe.${id}.label`,
      defaultMessage: farbeEntities[id].name,
    }),
    hex: t({ id: `farbe.${id}.hex` }),
  }));
  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  const Component = (
    <Box sx={{ px: 1 }}>
      <List {...{ value, options, setValueHandler, all }} />
    </Box>
  );
  return (
    <Filter
      component={Component}
      open={false}
      title={t({
        id: "filter.farbe_aussen.label",
        defaultMessage: "Außenfarbe",
      })}
    />
  );
};

export const SimpleFilter: React.FC<IFilter> = ({
  entities,
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();
  const { state: vehicles } = React.useContext(VehicleStore);

  const setValueHandler = (event: SelectChangeEvent) => {
    const value = parseInt(event.target.value);
    // Check if all variants are selected
    const newValue = value === 0 ? [] : [value];
    dispatchFilter({
      type: "UPDATE_FILTER",
      payload: { farbe_aussen: newValue },
    });
  };

  if (!(entities.farbe && vehicles)) return null;

  const items = Object.values(entities.farbe);

  const options = items
    .map(({ id, name }) => ({
      value: id,
      title: t({ id: `farbe.${id}.label`, defaultMessage: name }),
    }))
    .sort(sortAsc);

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  return (
    <FormControl size="small">
      <InputLabel id="filter-farbe-aussen-simple-label">
        <FormattedMessage
          id="filter.farbe.aussen-label"
          defaultMessage="Farbe"
          aussen-
        />
      </InputLabel>
      <Select
        labelId="filter-farbe-aussen-simple-label"
        id="filter-farbe-aussen-simple"
        value={value.length ? value[0].toString() : "0"}
        label={t({
          id: "filter.farbe.aussen-label",
          defaultMessage: "Außenfarbe",
        })}
        onChange={setValueHandler}
      >
        <MenuItem value={0}>{all.title}</MenuItem>
        {options.map(({ value, title }) => (
          <MenuItem key={"farbe-aussen-" + value} value={value}>
            {title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface IContainer {
  Pipe?: any;
  variant?: string;
}

const Container: React.FC<IContainer> = ({
  Pipe = Pipeline,
  variant = "side",
}) => {
  const Filter = variant === "side" ? SideFilter : SimpleFilter;
  return (
    <Pipe
      {...{
        type: "farbe_aussen",
        defaultValue: [],
        Component: Filter,
        Reducer: FarbeAussenReducer,
        Indicator: FarbeIndicator,
      }}
    />
  );
};

export default Container;
